import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Building2, Landmark, Truck, Cog } from 'lucide-react';
import { clients } from '../data/mock';

const typeIcons = {
  Banking: Landmark,
  Government: Building2,
  Logistics: Truck,
  Technology: Cog,
  Industry: Cog
};

const Clients = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { type: 'spring', stiffness: 200, damping: 20 }
    }
  };

  return (
    <section id="clients" className="clients-section" ref={ref}>
      <div className="clients-container">
        {/* Section Header */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="section-tag"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Our Clients
          </motion.span>
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Trusted by
            <span className="gradient-text"> Industry Leaders</span>
          </motion.h2>
          <motion.p 
            className="section-subtitle"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Serving major financial institutions and government organizations across India
          </motion.p>
        </motion.div>

        {/* Clients Grid */}
        <motion.div 
          className="clients-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {clients.map((client, index) => {
            const IconComponent = typeIcons[client.type] || Building2;
            return (
              <motion.div 
                key={client.id} 
                className="client-card"
                variants={cardVariants}
                whileHover={{ 
                  y: -8, 
                  boxShadow: '0 20px 50px rgba(59, 130, 246, 0.15)',
                  borderColor: '#3b82f6'
                }}
                style={{ opacity: 1 }}
                data-testid={`client-card-${client.id}`}
              >
                <motion.div 
                  className="client-icon"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <IconComponent size={40} />
                </motion.div>
                <h4 className="client-name">{client.name}</h4>
                <span className="client-type">{client.type}</span>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Trust Banner */}
        <motion.div 
          className="trust-banner"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          style={{ opacity: isInView ? 1 : 0 }}
        >
          <motion.div 
            className="trust-stat"
            whileHover={{ scale: 1.05 }}
          >
            <motion.span 
              className="trust-number"
              initial={{ opacity: 0, scale: 0 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.7, type: 'spring', stiffness: 200 }}
            >
              50+
            </motion.span>
            <span className="trust-label">Happy Clients</span>
          </motion.div>
          <div className="trust-divider"></div>
          <motion.div 
            className="trust-stat"
            whileHover={{ scale: 1.05 }}
          >
            <motion.span 
              className="trust-number"
              initial={{ opacity: 0, scale: 0 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.8, type: 'spring', stiffness: 200 }}
            >
              500+
            </motion.span>
            <span className="trust-label">Projects Delivered</span>
          </motion.div>
          <div className="trust-divider"></div>
          <motion.div 
            className="trust-stat"
            whileHover={{ scale: 1.05 }}
          >
            <motion.span 
              className="trust-number"
              initial={{ opacity: 0, scale: 0 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.9, type: 'spring', stiffness: 200 }}
            >
              100%
            </motion.span>
            <span className="trust-label">Client Satisfaction</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Clients;
